// Imports => React
import { useEffect, useMemo } from "react";
import { withStore } from "@stores";
import { observer } from "mobx-react-lite";
import clsx from "clsx";

// Imports => Utilities
import { AcIsSet, AcIsArray, AcSimplePagination } from "@opp/ui-kit";

// Imports => Atoms
import {
  AcContainer,
  AcRow,
  AcColumn,
  AcLoaderInline,
  AcEmptyBlock,
  AcPagination,
} from "@opp/ui-kit";
import AcTransactionList from "@components/ac-transaction-list/ac-transaction-list";
import { withTranslation } from "react-i18next";
import { AcGetPaginationTranslations } from "../../../utilities/ac-translate-consts";

const _CLASSES = {
  MAIN: "ac-transactions-overview-outgoing-tab",
};

const AcTransactionsOverviewOutgoingTab = ({ store: { movements }, t }) => {
  const { current_movements, current_meta, current_links, is_loading } =
    movements;

  const handlePagination = (event, num) => {
    const $scroller = document.querySelector("#ac-scroller");
    if ($scroller) {
      $scroller.scrollTop = 0;
    }

    movements.setPageNumber(num);
    movements.list(t, {
      perpage: 15,
      order: "-date",
      date_start: "01-01-2000",
      in: 0,
    });
  };

  useEffect(() => {
    const options = {
      perpage: 15,
      order: "-date",
      date_start: "01-01-2000",
      in: 0,
    };

    movements.setPageNumber(1);
    movements.list(t, options);
  }, []);

  const getMainClassNames = useMemo(() => {
    return clsx(_CLASSES.MAIN);
  });

  const renderTransactions = useMemo(() => {
    if (is_loading) {
      return <AcLoaderInline loading />;
    }

    if (
      !AcIsSet(current_movements) ||
      !AcIsArray(current_movements) ||
      current_movements.length === 0
    ) {
      return (
        <AcEmptyBlock
          message={t(
            "no-transactions-found",
            "No transactions found to display",
            { ns: "transactions" }
          )}
        />
      );
    }

    return <AcTransactionList items={current_movements} />;
  }, [current_movements, is_loading]);

  const renderPagination = useMemo(() => {
    if (is_loading) {
      return null;
    }

    if (
      !AcIsSet(current_movements) ||
      !AcIsArray(current_movements) ||
      current_movements.length === 0
    ) {
      return null;
    }

    // Normal pagination
    if (AcIsSet(current_meta.total)) {
      const options = {
        current_page: current_meta.current_page,
        last_page: current_meta.last_page,
        total_count: current_meta.total,
        total_pages: Math.ceil(current_meta.total / current_meta.per_page),
        per_page: current_meta.per_page,
        callback: handlePagination,
        from: current_meta.from,
        to: current_meta.to,
        showFromTo: true,
      };

      return (
        <AcPagination
          translations={AcGetPaginationTranslations(t)}
          {...options}
        />
      );
    } else {
      // Simple pagination
      const options = {
        current_page: current_meta.current_page,
        per_page: current_meta.per_page,
        callback: handlePagination,
        from: current_meta.from,
        to: current_meta.to,
        showFromTo: true,
        linkNext: current_links.links,
      };

      return (
        <AcSimplePagination
          translations={AcGetPaginationTranslations(t)}
          {...options}
        />
      );
    }
  }, [current_movements, current_meta, is_loading]);

  return (
    <div className={getMainClassNames}>
      <AcContainer fluid>
        <AcRow>
          <AcColumn xs={12}>{renderTransactions}</AcColumn>
        </AcRow>

        <AcRow className={"h-padding-bottom-50"}>
          <AcColumn xs={12}>{renderPagination}</AcColumn>
        </AcRow>
      </AcContainer>
    </div>
  );
};

export default withTranslation(["transactions", "pagination", "store"])(
  withStore(observer(AcTransactionsOverviewOutgoingTab))
);
